import * as React from 'react';
import { Box } from '@lcifr/components/box';
import { sx } from '@lcifr/sx';
import { Heading } from '@lcifr/components/heading';
import { Icon as IconLucifer } from '@lcifr/components/icon';
import { Section } from '@lcifr/components/section';
import { useScroll } from 'utils/hooks/useScroll';
import { alpha } from '@lcifr/theming';

export const Bg = sx(Box)(({ fadeOut }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  opacity: fadeOut ? 0 : 1,
  transition: '0.5s opacity ease-out',
  background:
    'linear-gradient(180deg, rgba(15, 17, 37, 0.1) 0%, rgba(15, 17, 37, 0.03) 100%)',
}));

export const Icon = sx(IconLucifer)(({ fadeOut }) => ({
  opacity: fadeOut ? 0 : 1,
  transition: '0.2s opacity ease-out',
  bg: 'background',
  p: 6,
  borderRadius: '50%',
  svg: {
    height: 54,
    width: 54,
  },
  boxShadow: '4px 4px 4px rgba(0,0,0,0.02)',
}));

type AdvantageListProps = {
  width?: any;
  minHeight?: any;
  bg?: any;
  bgImage?: any;
  spaceT?: any;
  spaceB?: any;
  stackSpace?: any;
  id?: any;
  vRhythm?: any;
  advantageList?: any;
};

export function AdvantageList({
  width,
  minHeight,
  bg,
  bgImage,
  spaceT,
  spaceB,
  stackSpace,
  id,
  vRhythm,
  advantageList,
}: AdvantageListProps) {
  const [isTop, setIsTop] = React.useState(false);
  useScroll({
    onScroll: (scrollPosition) => {
      setIsTop(scrollPosition.y >= 1 ? true : false);
    },
    active: true,
  });

  // const technology = useRawI18n()?.pageTechnology;
  const j = advantageList?.length;
  return j > 0 ? (
    <Section
      width="normal"
      minHeight={minHeight}
      bg={bg}
      bgImage={bgImage}
      spaceT={spaceT || 0}
      spaceB={spaceB || 8}
      stackSpace={0}
      id={id}
      vRhythm={vRhythm}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateAreas:
            j === 3
              ? [
                  ['"a1"', '"a2"', '"a3"'].join(' '),
                  ['"a1"', '"a2"', '"a3"'].join(' '),
                  ['"a1 a2"', '"a1 a3"'].join(' '),
                ]
              : j === 4
              ? [
                  ['"a1"', '"a2"', '"a3"', '"a4"'].join(' '),
                  ['"a1"', '"a2"', '"a3"', '"a4"'].join(' '),
                  ['"a1 a2"', '"a1 a3"', '"a1 a4"'].join(' '),
                ]
              : j === 5
              ? [
                  ['"a1"', '"a2"', '"a3"', '"a4"', '"a5"'].join(' '),
                  ['"a1"', '"a2"', '"a3"', '"a4"', '"a5"'].join(' '),
                  ['"a1 a2 a4"', '"a1 a3 a5"'].join(' '),
                ]
              : [
                  // j === 6
                  ['"a1"', '"a2"', '"a3"', '"a4"', '"a5"', '"a6"'].join(' '),
                  ['"a1"', '"a2"', '"a3"', '"a4"', '"a5"', '"a6"'].join(' '),
                  ['"a1 a2"', '"a3 a4"', '"a5 a6"'].join(' '),
                ],
          gap: '1px',
          minHeight: 400,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            right: '-1px',
            bottom: '-1px',
            backgroundColor: alpha('text', 0.1),
            zIndex: -1,
          },
        }}
      >
        {advantageList.map((advantage, i) => {
          return i === 0 && advantageList.length < 6 ? (
            <Box
              key={`a-${i}`}
              sx={{
                gridArea: `a${i + 1}`,
                py: [3, 3, 6],
                px: [3, 3, 6],
                bg: 'background',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: ['center', 'center', 'flex-start'],
                  gap: 4,
                }}
              >
                {advantage?.icon?.variant && (
                  <IconLucifer
                    name={advantage?.icon?.variant}
                    sx={{
                      bg: 'primary',
                      width: ['64px', '64px', '32px'],
                      height: ['64px', '64px', '32px'],
                      borderRadius: '50%',
                      p: 4,
                      flexShrink: 0,
                      svg: {
                        color: 'text.inverted',
                        width: '100%',
                        height: '100%',
                      },
                      path: {
                        fill:
                          (i === 0 || advantage?.icon?.variant === 'slate') &&
                          'currentColor !important',
                      },
                    }}
                  />
                )}
                <Heading
                  variant="h2"
                  sx={{ textAlign: ['center', 'center', 'left'] }}
                >
                  {advantage?.headline}
                </Heading>
              </Box>
            </Box>
          ) : (
            <Box
              key={`a-${i + 1}`}
              sx={{
                gridArea: `a${i + 1}`,
                py: [3, 3, 6],
                px: [3, 3, 6],
                bg: 'background',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <IconLucifer
                  name={advantage?.icon?.variant}
                  sx={{
                    bg: 'none',
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    p: 0,
                    svg: {
                      color: 'text',
                      width: 'inherit',
                      height: 'inherit',
                    },
                    path: {
                      fill:
                        advantage?.icon?.variant === 'slate' &&
                        'currentColor !important',
                    },
                  }}
                />
                <Heading variant="h5">{advantage?.headline}</Heading>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Section>
  ) : null;
}
